import React from 'react'
import Dropzone, { FileRejection } from 'react-dropzone'
import { toast } from 'react-toastify'

import { Icon } from '@clientbase/clientbase-library'
import { Box, Typography } from '@mui/material'

import Loader from 'components/Loader'

import { theme } from 'styles/theme'

import { DropContainer, UploadMessage, UploadProps } from '.'

const Upload: React.FC<UploadProps> = ({
  onUpload,
  maxFiles = 1,
  title,
  subTitle,
  uploadedFilesLength,
  sentIcon,
  disabled = false,
  accept,
  disabledText,
  variant = 'normal',
  error,
  isLoading,
  maxSize,
  invalidTypeMessage,
  tooManyFilesMessage
}) => {
  const renderDragMessage = (
    isDragActive: boolean,
    isDragReject: boolean,
    error?: boolean
  ) => {
    if (!isDragActive) {
      return (
        <Box display="flex" gap={variant === 'small' ? 0.5 : 1.5}>
          <Box display="flex" flexDirection="column" alignItems="center">
            {isLoading ? (
              <Loader />
            ) : (
              <Icon
                colorSX={
                  error ? theme.palette.error[300] : theme.palette.neutral[500]
                }
                icon="noteAdd"
                width={variant === 'small' ? '30px' : '48px'}
                height={variant === 'small' ? '34px' : '48px'}
              />
            )}
          </Box>
          <Box display="flex" flexDirection="column" justifyContent="center">
            <Typography
              variant={
                variant === 'small'
                  ? disabled
                    ? 'smLight'
                    : 'smBold'
                  : disabled
                  ? 'smLight'
                  : 'smBold'
              }
              color={disabled ? '#A3A6A2' : theme.palette.neutral[200]}
            >
              {!disabledText || isLoading
                ? title
                : disabled
                ? disabledText
                : title}
            </Typography>
            <Typography
              variant="xsLight"
              color={
                error ? theme.palette.error[300] : theme.palette.neutral[500]
              }
              sx={{
                display: !disabledText ? 'block' : disabled ? 'none' : 'auto'
              }}
            >
              {subTitle}
            </Typography>
          </Box>
        </Box>
      )
    }

    if (isDragReject) {
      return <UploadMessage type="error">Arquivo não suportado</UploadMessage>
    }

    return <UploadMessage type="success">Solte os arquivos aqui</UploadMessage>
  }

  const handleDropRejected = (fileRejections: FileRejection[]) => {
    const first = fileRejections[0]
    const code = first?.errors?.[0]?.code
    let message = 'Não foi possível enviar o arquivo.'
    if (code === 'file-invalid-type') {
      message =
        invalidTypeMessage ||
        'Tipo de arquivo não suportado. Envie uma imagem ou PDF.'
    } else if (code === 'too-many-files') {
      message =
        tooManyFilesMessage ||
        `Você pode enviar no máximo ${maxFiles} arquivo(s).`
    } else if (code === 'file-too-large') {
      const sizeMb = maxSize ? Math.round(maxSize / (1024 * 1024)) : null
      message = sizeMb
        ? `Cada arquivo deve ter no máximo ${sizeMb}MB.`
        : 'Arquivo muito grande.'
    }
    toast.error(message)
  }

  return (
    <Dropzone
      accept={
        accept
          ? accept
          : {
              'image/*': ['.png', '.jpg', '.jpeg'],
              'application/pdf': ['.pdf'],
              'application/msword': ['.doc'],
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                ['.docx'],
              'text/csv': ['.csv']
            }
      }
      disabled={disabled}
      onDropAccepted={onUpload}
      onDropRejected={handleDropRejected}
      maxFiles={maxFiles}
      maxSize={maxSize}
    >
      {({ getRootProps, getInputProps, isDragActive, isDragReject }) =>
        !sentIcon ? (
          <DropContainer
            {...getRootProps()}
            isDragActive={isDragActive}
            isDragReject={isDragReject}
            error={error}
            sx={{
              cursor: disabled ? 'default' : 'pointer',
              ...(variant === 'small' && {
                padding: '10px'
              })
            }}
          >
            <input {...getInputProps()} />
            <Box />
            {renderDragMessage(isDragActive, isDragReject, error)}
          </DropContainer>
        ) : uploadedFilesLength !== maxFiles ? (
          <DropContainer
            {...getRootProps()}
            isDragActive={isDragActive}
            isDragReject={isDragReject}
            sx={{ cursor: disabled ? 'default' : 'pointer' }}
          >
            <input {...getInputProps()} />
            <Box />
            {renderDragMessage(isDragActive, isDragReject)}
          </DropContainer>
        ) : (
          <DropContainer
            isDragActive={isDragActive}
            isDragReject={isDragReject}
            sucess={true}
            sx={{ cursor: disabled ? 'default' : 'pointer' }}
          >
            <Box display="flex" justifyContent="space-between">
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
              >
                <Typography variant="smBold">{title}</Typography>
                <Typography
                  variant="xsLight"
                  color={theme.palette.neutral[500]}
                  mt="8px"
                >
                  {subTitle}
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" alignItems="center">
                <Icon
                  colorSX={theme.palette.success[300]}
                  icon="noteAdd"
                  width="40px"
                  height="40px"
                />
                <Typography
                  variant="xsLight"
                  color={theme.palette.success[300]}
                  mt="8px"
                >
                  Enviado
                </Typography>
              </Box>
            </Box>
          </DropContainer>
        )
      }
    </Dropzone>
  )
}

export default Upload
